import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { financialStatementsKeys } from './financialStatementsQueryKeys'
import type {
  FinancialStatementsResponse,
  FinancialStatementsFilterKeys,
  EnhancedFinancialStatement,
  Dimension
} from '../types'
import {
  normalizeStatementRows,
  addDimensionGroups,
  addInvestmentMenuRows,
  addAmendmentMenuRows,
  addCounterAmendmentMenuRows,
  addReallocationMenuRows
} from '../utils/dataEnhancement'
import { pipeEnhancers } from '../utils/pipeEnhancers'
import { toFilterParams } from '../utils/filterConverters'
import { fetchFinancialStatementData } from '../services/financialStatementsService'

// Generic types for flexibility
type SelectorFunction<TData> = (data: FinancialStatementsResponse) => TData

export const useFinancialStatementsQuery = <
  TData = EnhancedFinancialStatement // Default to EnhancedFinancialStatement
>(
  filterKeys: FinancialStatementsFilterKeys,
  dimensionMap: Map<string | null, Dimension[]> | undefined,
  isBudgetingPage = false,
  options?: {
    enabled?: boolean
    staleTime?: number
    gcTime?: number
  }
) => {
  // Derive filter params from filter keys
  const filterParams = useMemo(() => toFilterParams(filterKeys), [filterKeys])
  const { dimension = '' } = filterParams

  // Optimize selector function to prevent unnecessary re-renders
  const selectData = useMemo(() => {
    // Cache the default selector - map each statement through normalizeStatementRows
    return (response: FinancialStatementsResponse): EnhancedFinancialStatement => {
      const rawData = response[0].data

      // For reporting page (non-budgeting), return plain normalized data without enhancements
      // This improves performance by skipping unnecessary menu rows for investments, amendments, etc.
      if (!isBudgetingPage) {
        return {
          incomeStatement: normalizeStatementRows(rawData.incomeStatement, [], 'incomeStatement', dimension),
          assets: normalizeStatementRows(rawData.assets, [], 'assets'),
          liabilities: normalizeStatementRows(rawData.liabilities, [], 'liabilities')
        }
      }

      // For budgeting page, apply full enhancement pipeline
      // Income statement pipeline: normalize → add dimension groups → add reallocations → add counter amendment menus
      const incomeStatementEnhances = [addReallocationMenuRows, addCounterAmendmentMenuRows]

      if (dimensionMap?.has(dimension)) {
        incomeStatementEnhances.push(addDimensionGroups)
      }
      const enhanceIncomeStatement = pipeEnhancers(...incomeStatementEnhances)

      // Balance sheet pipeline: normalize → add investment menus → add amendment menus → add counter amendment menus
      const enhanceBalanceSheet = pipeEnhancers(
        addInvestmentMenuRows,
        addAmendmentMenuRows,
        addCounterAmendmentMenuRows
      )

      return {
        incomeStatement: enhanceIncomeStatement(
          normalizeStatementRows(rawData.incomeStatement, [], 'incomeStatement', dimension)
        ),
        assets: enhanceBalanceSheet(normalizeStatementRows(rawData.assets, [], 'assets')),
        liabilities: enhanceBalanceSheet(normalizeStatementRows(rawData.liabilities, [], 'liabilities'))
      }
    }
  }, [dimension, dimensionMap, isBudgetingPage]) as SelectorFunction<TData>

  return useQuery({
    queryKey: financialStatementsKeys.statementData(filterKeys),
    queryFn: fetchFinancialStatementData(filterParams),
    enabled: !!filterKeys.companyId && (options?.enabled ?? true),
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
    gcTime: options?.gcTime ?? 10 * 60 * 1000, // 10 minutes
    select: selectData,
    // Add performance optimizations
    structuralSharing: true,
    refetchOnWindowFocus: false
  })
}
